import { useEffect } from 'react'
import type { ReactElement } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { usePatientStore } from '@/stores/patientStore'
import { useEmployeeStore } from '@/stores/employeeStore'
import { useDiaryStore } from '@/stores/diaryStore'
import { useFooterStore } from '@/stores/footerStore'
import { useSelfMedicationStore } from '@/stores/selfMedicationStore'
import { reportAppLaunch, resetAppUsageSession } from '@/utils/appUsage'

/**
 * Handles app-level data initialisation.
 * Replaces the useEffect-based fetching that used to live in PatientProvider and EmployeeProvider.
 * Rendered once inside the BrowserRouter so hooks (useMessage) are available.
 */
export default function AppInit(): ReactElement | null {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const bootstrap = useAuthStore((s) => s.bootstrap)
  const emailAddress = usePatientStore((s) => s.patientInfo?.user?.emailAddress ?? null)

  // On first mount, probe the backend (whoami) to learn whether the HttpOnly
  // cookie authenticates us — JS cannot read the cookie itself.
  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  // Fetch data when we become authenticated (login / page refresh) and reset
  // when we log out.
  useEffect(() => {
    if (!isAuthenticated) {
      usePatientStore.getState().reset()
      useEmployeeStore.getState().reset()
      useDiaryStore.getState().reset()
      useFooterStore.getState().reset()
      useSelfMedicationStore.getState().reset()
      resetAppUsageSession()
      return
    }

    usePatientStore.getState().fetchPatientInfo()
    useEmployeeStore.getState().fetchEmployees()
  }, [isAuthenticated])

  // Report once per session how the portal was opened (installed app or
  // browser). Needs a session, so it waits for authentication.
  useEffect(() => {
    if (!isAuthenticated) return
    void reportAppLaunch()
  }, [isAuthenticated])

  // Bind device-local stores to the patient identity, also across account switches
  useEffect(() => {
    useSelfMedicationStore.getState().setUser(emailAddress)
  }, [emailAddress])

  return null
}
